/**
 * Resolve an FDD queue item to a runnable shape.
 *
 * Called by every FDD tool's run handler before reading the queue item's
 * SharePoint refs. The motivation: the VDR upload pipeline TRIES to stamp
 * `driveId` / `fileId` onto the queue item via `promotePendingUploadByFileName`
 * once the upload finishes. That sometimes doesn't fire (skipped uploads,
 * missing existing-file refs, edge cases). If we relied solely on that
 * promotion path, the FDD tool would refuse to run with a "no file refs"
 * error — even though the file IS in the project's SharePoint drive.
 *
 * This helper closes that gap unconditionally:
 *   1. If the queue item already has refs (or a local File blob), keep them.
 *   2. Otherwise, search the project's SharePoint drive for an exact-name
 *      match and stamp the refs onto the queue item.
 *   3. Return the (possibly updated) item.
 *
 * If no SP folder is linked or the search returns no exact match, the
 * item is returned unchanged — the caller decides how to handle that
 * (typically: throw a clear "queue this from the SP picker" error).
 */

import type { FDDQueueItem } from "../types";
import { getFileBlob, promotePendingUploadByFileName } from "../store/fddStore";
import { getFolderRef } from "../../store/folderStore";
import { searchSharePointForFile } from "../../utils/sharePointSearch";

export async function ensureSharePointRefs(
  item: FDDQueueItem,
  projectId: string,
): Promise<FDDQueueItem> {
  // Local blob still in memory? Tool runs from that, no SP needed.
  if (getFileBlob(item.id)) return item;

  // Already has refs? Nothing to do.
  if (item.driveId && item.fileId) return item;

  // Need a SharePoint root to search inside.
  const folder = getFolderRef(projectId);
  if (!folder) return item;

  const found = await searchSharePointForFile(folder.driveId, folder.itemId, item.fileName);
  if (!found) return item;

  // Stamp the queue item so the next run is instant — and so the FDD
  // panel re-renders out of "waiting to upload" once it re-reads state.
  promotePendingUploadByFileName(projectId, item.fileName, {
    driveId: found.driveId,
    fileId: found.itemId,
    fileWebUrl: found.webUrl,
    fileSize: found.size,
  });

  return {
    ...item,
    status: "pending",
    driveId: found.driveId,
    fileId: found.itemId,
    fileWebUrl: found.webUrl,
    fileSize: found.size ?? item.fileSize,
  };
}
